import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { FeedbackFields } from "../lib/types";
import { display } from "../lib/format";
import { Card, Button, Spinner, ErrorNote } from "../components/ui";

export default function FeedbackDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["feedbacks", id],
    queryFn: () => api.feedbacks.get(id),
    enabled: !!id,
  });

  const remove = useMutation({
    mutationFn: () => api.feedbacks.remove(id),
    onSuccess: () => {
      // Invalidate the list so the removed row is gone when we land back on
      // it, rather than reappearing from cache.
      qc.invalidateQueries({ queryKey: ["feedbacks"] });
      navigate("/feedbacks");
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <div className="max-w-2xl">
      <Link to="/feedbacks" className="text-sm text-muted hover:underline">
        ← Feedbacks
      </Link>

      <header className="mb-4 mt-2 flex items-start justify-between gap-4">
        <h1 className="text-xl font-semibold">Feedback</h1>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            if (confirm("Delete this feedback? This cannot be undone.")) remove.mutate();
          }}
        >
          Delete
        </Button>
      </header>

      {remove.error && <ErrorNote error={remove.error} />}

      <Card className="divide-y divide-hairline">
        {FeedbackFields.map((f) => (
          <div key={f.name} className="flex gap-4 px-4 py-3 text-sm">
            <dt className="w-40 shrink-0 text-muted">{f.label}</dt>
            <dd className="min-w-0 break-words">
              {display((data as Record<string, unknown>)[f.name], f.type)}
            </dd>
          </div>
        ))}
      </Card>
    </div>
  );
}
